// ============================================================================
// Contacts extras · expense retention / duplicates / PDF split · payment
// allocations · API keys · company switcher — UI strings (es / en / ca).
// Registered straight into i18next on import (same pattern as
// locales/documentMail.js) so the big locale JSON files are not touched.
// ============================================================================
import i18n from '../lib/i18n';

const es = {
  contactExtra: {
    tradeName: 'Nombre comercial',
    website: 'Sitio web',
    docType: 'Tipo de documento',
    docTypeHint: 'NIF/CIF o NIE para España; NIF-IVA para empresas de la UE; pasaporte u otro para el resto (Verifactu lo necesita).',
    defaultTaxRate: 'IVA por defecto',
    defaultTaxRateNone: 'El general de la cuenta',
    defaultTaxRateHint: 'Tipo con el que empiezan las líneas nuevas para este contacto.',
    defaultExpenseCategory: 'Categoría de gasto por defecto',
    defaultExpenseCategoryNone: 'Sin categoría por defecto',
    ccEmailMulti: 'CC en cada email (varios separados por comas)',
    readyToInvoice: 'Listo para facturar',
    notReady: 'Faltan datos: {{missing}}',
    missingName: 'nombre', missingTaxId: 'NIF', missingAddress: 'dirección', missingPostalCode: 'código postal', missingCity: 'población',
    needsMigration: 'Estos campos se activan al aplicar backend/contacts_payments_expenses.sql.',
  },
  expenses: {
    retention: 'Retención IRPF',
    retentionNone: 'Sin retención',
    retentionRate: 'Retención ({{rate}} %)',
    netToPay: 'A pagar al proveedor: {{amount}}',
    retentionHint: 'Profesionales (abogados, arquitectos…) facturan con retención: pagas base + IVA − retención y la ingresas en el modelo 111.',
    duplicateTitle: 'Posible gasto duplicado',
    duplicateBody: 'Ya existe un gasto de {{supplier}} con el número {{number}} ({{amount}}, {{date}}). ¿Guardar de todos modos?',
    duplicateBodyNoNumber: 'Ya existe un gasto de {{supplier}} por {{amount}} con fecha {{date}}. ¿Guardar de todos modos?',
    splitButton: 'Dividir PDF',
    splitTitle: 'Dividir un PDF en varios gastos',
    splitIntro: 'Un PDF con varias facturas (un lote mensual, un escaneo de varios tickets) se corta en partes; cada parte se lee con el escáner y se crea como un gasto «A revisar» con su propio archivo.',
    splitFile: 'Archivo PDF',
    splitMode: 'Cómo dividir',
    splitPerPage: 'Una factura por página',
    splitRanges: 'Por rangos de páginas',
    splitRangesPh: 'Ej.: 1-2, 3, 4-6',
    splitRun: 'Dividir y crear gastos',
    splitRunning: 'Dividiendo…',
    splitDone: 'Creados {{count}} gastos «A revisar» a partir de {{parts}} partes.',
    splitDoneNoOcr: 'Creados {{count}} gastos sin importe (escáner no disponible): complétalos al revisar.',
    splitError: 'No se pudo dividir: {{message}}',
    splitPlan: 'La división usa el escáner con IA de tu plan; si no está incluido, los gastos se crean vacíos para que los completes.',
  },
  payments: {
    treasury: 'Cuenta de tesorería',
    treasuryNone: 'Sin indicar',
    treasuryBank: 'Banco',
    treasuryCash: 'Caja',
    treasuryCard: 'TPV / tarjeta',
    reconciled: 'Conciliado',
    reconciledHint: 'Marcado cuando el movimiento aparece en el banco.',
    onAccount: 'A cuenta',
    onAccountHint: 'Cobro sin factura: queda a favor del cliente y se ve en su ficha y en el libro mayor.',
    allocateButton: 'Repartir entre varias facturas',
    allocateTitle: 'Repartir el cobro entre facturas',
    allocateIntro: 'Elige qué facturas pendientes de {{client}} salda este cobro. El importe se reparte de la más antigua a la más reciente; puedes cambiar cada cantidad.',
    allocateAmount: 'Importe del cobro',
    allocateAuto: 'Repartir automáticamente',
    allocatePending: 'Pendiente',
    allocateApply: 'Aplicar',
    allocateRemaining: 'Sin asignar: {{amount}}',
    allocateOver: 'Has asignado {{amount}} más de lo cobrado.',
    allocateNone: 'Este cliente no tiene facturas pendientes; el cobro quedará a cuenta.',
    allocateSave: 'Guardar reparto',
    allocatedTo: 'Repartido entre {{count}} facturas',
    allocatedOne: '{{number}} y {{count}} más',
    colTreasury: 'Tesorería',
    needsMigration: 'El reparto entre facturas y la tesorería se activan al aplicar backend/contacts_payments_expenses.sql.',
  },
  api: {
    title: 'API pública',
    intro: 'Conecta tu tienda online, tu TPV o cualquier programa con zBilling. Con una clave puedes crear y leer facturas, presupuestos, contactos, productos, cobros y gastos de esta cuenta.',
    keysTitle: 'Claves de acceso',
    newKey: 'Nueva clave',
    keyName: 'Nombre (para qué es)',
    keyNamePh: 'Tienda online, zPOS, Zapier…',
    scopes: 'Permisos',
    scopeRead: 'Leer',
    scopeWrite: 'Crear y modificar',
    create: 'Crear clave',
    creating: 'Creando…',
    created: 'Clave creada. Cópiala ahora: no volverá a mostrarse.',
    copy: 'Copiar',
    copied: 'Copiada',
    revoke: 'Revocar',
    revokeConfirm: '¿Revocar la clave «{{name}}»? Los programas que la usen dejarán de funcionar.',
    revoked: 'Revocada',
    lastUsed: 'Último uso: {{when}}',
    neverUsed: 'Nunca usada',
    requests: '{{count}} peticiones',
    empty: 'Aún no hay claves.',
    ownerOnly: 'Sólo el titular de la cuenta gestiona las claves.',
    needsMigration: 'La API se activa al aplicar backend/api_keys.sql.',
    docsTitle: 'Cómo se usa',
    docsBase: 'URL base',
    docsAuth: 'Cabecera',
    docsResources: 'Recursos',
    docsExample: 'Ejemplo: crear y emitir una factura',
    docsLimits: 'Límites: 120 peticiones por minuto y clave; listas de hasta 200 elementos con ?limit= y ?offset=. Las facturas emitidas por la API se registran en Verifactu como las demás.',
    docsFull: 'Documentación interactiva (OpenAPI)',
    error: 'API: {{message}}',
  },
  accounts: {
    title: 'Mis empresas',
    current: 'Actual',
    switch: 'Cambiar',
    add: 'Añadir otra empresa',
    addHint: 'Inicia sesión en otra cuenta de zBilling para tenerla a un clic. Cada empresa conserva sus datos, su plan y sus usuarios.',
    email: 'Email de la otra cuenta',
    password: 'Contraseña',
    signIn: 'Entrar y añadir',
    signingIn: 'Entrando…',
    remove: 'Quitar de la lista',
    switching: 'Cambiando de empresa…',
    expired: 'La sesión de {{email}} caducó: vuelve a introducir su contraseña.',
    error: 'No se pudo cambiar: {{message}}',
    createNew: 'Crear una empresa nueva',
    createNewHint: 'Se registra como otra cuenta (con otro email) y luego la añades aquí.',
  },
  settingsNav: { api: 'API' },
  usermenu: { accounts: 'Mis empresas' },
};

const en = {
  contactExtra: {
    tradeName: 'Trade name',
    website: 'Website',
    docType: 'ID document type',
    docTypeHint: 'NIF/CIF or NIE for Spain; VAT number (NIF-IVA) for EU companies; passport or other elsewhere (Verifactu needs it).',
    defaultTaxRate: 'Default tax rate',
    defaultTaxRateNone: 'The account default',
    defaultTaxRateHint: 'Rate a new line starts with for this contact.',
    defaultExpenseCategory: 'Default expense category',
    defaultExpenseCategoryNone: 'No default category',
    ccEmailMulti: 'CC on every e-mail (several, comma-separated)',
    readyToInvoice: 'Ready to invoice',
    notReady: 'Missing: {{missing}}',
    missingName: 'name', missingTaxId: 'tax ID', missingAddress: 'address', missingPostalCode: 'postal code', missingCity: 'city',
    needsMigration: 'These fields become available once backend/contacts_payments_expenses.sql is applied.',
  },
  expenses: {
    retention: 'Withholding tax',
    retentionNone: 'No withholding',
    retentionRate: 'Withholding ({{rate}} %)',
    netToPay: 'To pay the supplier: {{amount}}',
    retentionHint: 'Professionals invoice with withholding tax: you pay base + VAT − withholding and file the withheld amount.',
    duplicateTitle: 'Possible duplicate expense',
    duplicateBody: 'An expense from {{supplier}} with number {{number}} ({{amount}}, {{date}}) already exists. Save anyway?',
    duplicateBodyNoNumber: 'An expense from {{supplier}} for {{amount}} dated {{date}} already exists. Save anyway?',
    splitButton: 'Split PDF',
    splitTitle: 'Split one PDF into several expenses',
    splitIntro: 'A PDF holding several invoices (a monthly bundle, a scan of several receipts) is cut into parts; each part is read by the scanner and created as its own expense pending review, with its own file.',
    splitFile: 'PDF file',
    splitMode: 'How to split',
    splitPerPage: 'One invoice per page',
    splitRanges: 'By page ranges',
    splitRangesPh: 'E.g. 1-2, 3, 4-6',
    splitRun: 'Split and create expenses',
    splitRunning: 'Splitting…',
    splitDone: 'Created {{count}} expenses pending review from {{parts}} parts.',
    splitDoneNoOcr: 'Created {{count}} expenses without amounts (scanner unavailable): complete them while reviewing.',
    splitError: 'Could not split: {{message}}',
    splitPlan: 'Splitting uses your plan\'s AI scanner; without it the expenses are created empty for you to complete.',
  },
  payments: {
    treasury: 'Treasury account',
    treasuryNone: 'Not set',
    treasuryBank: 'Bank',
    treasuryCash: 'Cash',
    treasuryCard: 'Card terminal',
    reconciled: 'Reconciled',
    reconciledHint: 'Ticked when the movement shows up on the bank statement.',
    onAccount: 'On account',
    onAccountHint: 'Payment without an invoice: stays as a credit for the customer, visible on their card and in the ledger.',
    allocateButton: 'Split across several invoices',
    allocateTitle: 'Allocate the payment to invoices',
    allocateIntro: 'Choose which outstanding invoices of {{client}} this payment settles. The amount is spread oldest first; you can change each figure.',
    allocateAmount: 'Payment amount',
    allocateAuto: 'Allocate automatically',
    allocatePending: 'Outstanding',
    allocateApply: 'Apply',
    allocateRemaining: 'Unallocated: {{amount}}',
    allocateOver: 'You allocated {{amount}} more than was paid.',
    allocateNone: 'This customer has no outstanding invoices; the payment stays on account.',
    allocateSave: 'Save allocation',
    allocatedTo: 'Allocated to {{count}} invoices',
    allocatedOne: '{{number}} and {{count}} more',
    colTreasury: 'Treasury',
    needsMigration: 'Allocations and treasury accounts become available once backend/contacts_payments_expenses.sql is applied.',
  },
  api: {
    title: 'Public API',
    intro: 'Connect your online shop, your till or any program to zBilling. With a key you can create and read invoices, quotes, contacts, products, payments and expenses of this account.',
    keysTitle: 'Access keys',
    newKey: 'New key',
    keyName: 'Name (what it is for)',
    keyNamePh: 'Online shop, zPOS, Zapier…',
    scopes: 'Permissions',
    scopeRead: 'Read',
    scopeWrite: 'Create and update',
    create: 'Create key',
    creating: 'Creating…',
    created: 'Key created. Copy it now: it will not be shown again.',
    copy: 'Copy',
    copied: 'Copied',
    revoke: 'Revoke',
    revokeConfirm: 'Revoke key “{{name}}”? Programs using it will stop working.',
    revoked: 'Revoked',
    lastUsed: 'Last used: {{when}}',
    neverUsed: 'Never used',
    requests: '{{count}} requests',
    empty: 'No keys yet.',
    ownerOnly: 'Only the account owner manages keys.',
    needsMigration: 'The API becomes available once backend/api_keys.sql is applied.',
    docsTitle: 'How to use it',
    docsBase: 'Base URL',
    docsAuth: 'Header',
    docsResources: 'Resources',
    docsExample: 'Example: create and issue an invoice',
    docsLimits: 'Limits: 120 requests per minute per key; lists of up to 200 items with ?limit= and ?offset=. Invoices issued through the API are registered with Verifactu like any other.',
    docsFull: 'Interactive documentation (OpenAPI)',
    error: 'API: {{message}}',
  },
  accounts: {
    title: 'My companies',
    current: 'Current',
    switch: 'Switch',
    add: 'Add another company',
    addHint: 'Sign in to another zBilling account to keep it one click away. Each company keeps its own data, plan and users.',
    email: 'E-mail of the other account',
    password: 'Password',
    signIn: 'Sign in and add',
    signingIn: 'Signing in…',
    remove: 'Remove from list',
    switching: 'Switching company…',
    expired: 'The session of {{email}} expired: enter its password again.',
    error: 'Could not switch: {{message}}',
    createNew: 'Create a new company',
    createNewHint: 'Registers as another account (another e-mail); then add it here.',
  },
  settingsNav: { api: 'API' },
  usermenu: { accounts: 'My companies' },
};

const ca = {
  contactExtra: {
    tradeName: 'Nom comercial',
    website: 'Lloc web',
    docType: 'Tipus de document',
    docTypeHint: 'NIF/CIF o NIE per a Espanya; NIF-IVA per a empreses de la UE; passaport o altre per a la resta (Verifactu ho necessita).',
    defaultTaxRate: 'IVA per defecte',
    defaultTaxRateNone: 'El general del compte',
    defaultTaxRateHint: 'Tipus amb què comencen les línies noves per a aquest contacte.',
    defaultExpenseCategory: 'Categoria de despesa per defecte',
    defaultExpenseCategoryNone: 'Sense categoria per defecte',
    ccEmailMulti: 'CC a cada correu (diversos separats per comes)',
    readyToInvoice: 'A punt per facturar',
    notReady: 'Falten dades: {{missing}}',
    missingName: 'nom', missingTaxId: 'NIF', missingAddress: 'adreça', missingPostalCode: 'codi postal', missingCity: 'població',
    needsMigration: 'Aquests camps s\'activen en aplicar backend/contacts_payments_expenses.sql.',
  },
  expenses: {
    retention: 'Retenció IRPF',
    retentionNone: 'Sense retenció',
    retentionRate: 'Retenció ({{rate}} %)',
    netToPay: 'A pagar al proveïdor: {{amount}}',
    retentionHint: 'Els professionals facturen amb retenció: pagues base + IVA − retenció i la ingresses al model 111.',
    duplicateTitle: 'Possible despesa duplicada',
    duplicateBody: 'Ja hi ha una despesa de {{supplier}} amb el número {{number}} ({{amount}}, {{date}}). Desar de tota manera?',
    duplicateBodyNoNumber: 'Ja hi ha una despesa de {{supplier}} per {{amount}} amb data {{date}}. Desar de tota manera?',
    splitButton: 'Dividir PDF',
    splitTitle: 'Dividir un PDF en diverses despeses',
    splitIntro: 'Un PDF amb diverses factures (un lot mensual, un escaneig de diversos tiquets) es talla en parts; cada part es llegeix amb l\'escàner i es crea com una despesa «A revisar» amb el seu propi fitxer.',
    splitFile: 'Fitxer PDF',
    splitMode: 'Com dividir',
    splitPerPage: 'Una factura per pàgina',
    splitRanges: 'Per rangs de pàgines',
    splitRangesPh: 'Ex.: 1-2, 3, 4-6',
    splitRun: 'Dividir i crear despeses',
    splitRunning: 'Dividint…',
    splitDone: 'Creades {{count}} despeses «A revisar» a partir de {{parts}} parts.',
    splitDoneNoOcr: 'Creades {{count}} despeses sense import (escàner no disponible): completa-les en revisar.',
    splitError: 'No s\'ha pogut dividir: {{message}}',
    splitPlan: 'La divisió fa servir l\'escàner amb IA del teu pla; si no hi és, les despeses es creen buides perquè les completis.',
  },
  payments: {
    treasury: 'Compte de tresoreria',
    treasuryNone: 'Sense indicar',
    treasuryBank: 'Banc',
    treasuryCash: 'Caixa',
    treasuryCard: 'TPV / targeta',
    reconciled: 'Conciliat',
    reconciledHint: 'Marcat quan el moviment apareix al banc.',
    onAccount: 'A compte',
    onAccountHint: 'Cobrament sense factura: queda a favor del client i es veu a la seva fitxa i al llibre major.',
    allocateButton: 'Repartir entre diverses factures',
    allocateTitle: 'Repartir el cobrament entre factures',
    allocateIntro: 'Tria quines factures pendents de {{client}} salda aquest cobrament. L\'import es reparteix de la més antiga a la més recent; pots canviar cada quantitat.',
    allocateAmount: 'Import del cobrament',
    allocateAuto: 'Repartir automàticament',
    allocatePending: 'Pendent',
    allocateApply: 'Aplicar',
    allocateRemaining: 'Sense assignar: {{amount}}',
    allocateOver: 'Has assignat {{amount}} més del que s\'ha cobrat.',
    allocateNone: 'Aquest client no té factures pendents; el cobrament quedarà a compte.',
    allocateSave: 'Desar repartiment',
    allocatedTo: 'Repartit entre {{count}} factures',
    allocatedOne: '{{number}} i {{count}} més',
    colTreasury: 'Tresoreria',
    needsMigration: 'El repartiment entre factures i la tresoreria s\'activen en aplicar backend/contacts_payments_expenses.sql.',
  },
  api: {
    title: 'API pública',
    intro: 'Connecta la teva botiga en línia, el teu TPV o qualsevol programa amb zBilling. Amb una clau pots crear i llegir factures, pressupostos, contactes, productes, cobraments i despeses d\'aquest compte.',
    keysTitle: 'Claus d\'accés',
    newKey: 'Nova clau',
    keyName: 'Nom (per a què és)',
    keyNamePh: 'Botiga en línia, zPOS, Zapier…',
    scopes: 'Permisos',
    scopeRead: 'Llegir',
    scopeWrite: 'Crear i modificar',
    create: 'Crear clau',
    creating: 'Creant…',
    created: 'Clau creada. Copia-la ara: no es tornarà a mostrar.',
    copy: 'Copiar',
    copied: 'Copiada',
    revoke: 'Revocar',
    revokeConfirm: 'Revocar la clau «{{name}}»? Els programes que la facin servir deixaran de funcionar.',
    revoked: 'Revocada',
    lastUsed: 'Darrer ús: {{when}}',
    neverUsed: 'Mai usada',
    requests: '{{count}} peticions',
    empty: 'Encara no hi ha claus.',
    ownerOnly: 'Només el titular del compte gestiona les claus.',
    needsMigration: 'L\'API s\'activa en aplicar backend/api_keys.sql.',
    docsTitle: 'Com es fa servir',
    docsBase: 'URL base',
    docsAuth: 'Capçalera',
    docsResources: 'Recursos',
    docsExample: 'Exemple: crear i emetre una factura',
    docsLimits: 'Límits: 120 peticions per minut i clau; llistes de fins a 200 elements amb ?limit= i ?offset=. Les factures emeses per l\'API es registren a Verifactu com les altres.',
    docsFull: 'Documentació interactiva (OpenAPI)',
    error: 'API: {{message}}',
  },
  accounts: {
    title: 'Les meves empreses',
    current: 'Actual',
    switch: 'Canviar',
    add: 'Afegir una altra empresa',
    addHint: 'Inicia sessió en un altre compte de zBilling per tenir-lo a un clic. Cada empresa conserva les seves dades, el seu pla i els seus usuaris.',
    email: 'Correu de l\'altre compte',
    password: 'Contrasenya',
    signIn: 'Entrar i afegir',
    signingIn: 'Entrant…',
    remove: 'Treure de la llista',
    switching: 'Canviant d\'empresa…',
    expired: 'La sessió de {{email}} ha caducat: torna a introduir-ne la contrasenya.',
    error: 'No s\'ha pogut canviar: {{message}}',
    createNew: 'Crear una empresa nova',
    createNewHint: 'Es registra com un altre compte (amb un altre correu) i després l\'afegeixes aquí.',
  },
  settingsNav: { api: 'API' },
  usermenu: { accounts: 'Les meves empreses' },
};

for (const [lng, res] of [['es', es], ['en', en], ['ca', ca]]) {
  i18n.addResourceBundle(lng, 'translation', res, true, false);
}

export { es, en, ca };
